import { api } from "./api";

// Files are sliced into parts. With direct uploads enabled on the server each
// part goes straight to R2 through a signed URL; otherwise it is sent through the
// API (one request per part, kept under Cloudflare's 100 MB request limit).
const WORKER_PART_SIZE = 50 * 1024 * 1024;
const DIRECT_PART_SIZE = 32 * 1024 * 1024;
const WORKER_CONCURRENCY = 3;
const DIRECT_CONCURRENCY = 4;
const MAX_RETRIES = 4;
const STALL_MS = 40 * 1000; // no bytes moving for this long = retry the part

export function formatBytes(bytes) {
  if (!bytes) return "";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 1 : 2)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
}

class DirectUnavailable extends Error {}

const abortError = () => new DOMException("Upload cancelled", "AbortError");
const isAbort = (err) => err?.name === "AbortError" || err?.code === "ERR_CANCELED";

// PUT one part straight to R2 (no cookies/auth headers — the URL is the credential)
function putToR2(url, blob, part, { signal, onBytes }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onBytes(e.loaded);
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        if (etag) resolve({ part_number: part, etag });
        else reject(Object.assign(new Error("no-etag"), { kind: "cors" }));
      } else reject(Object.assign(new Error(`HTTP ${xhr.status}`), { kind: "http" }));
    };
    xhr.onerror = () => reject(Object.assign(new Error("network"), { kind: "cors" }));
    signal.addEventListener("abort", () => { xhr.abort(); reject(abortError()); }, { once: true });
    xhr.send(blob);
  });
}

/**
 * Uploads a (potentially multi-GB) file straight into our R2 storage.
 * Resolves with { url, size }. Rejects with an Error whose message is user-facing;
 * an aborted upload rejects with error.name === "AbortError".
 * onProgress(fraction, bytesPerSecond) is called as bytes move.
 */
export async function uploadLargeFile(file, { onProgress, signal } = {}) {
  const contentType = file.type || "application/octet-stream";
  try {
    return await run(file, { onProgress, signal, contentType, wantDirect: true });
  } catch (err) {
    if (!(err instanceof DirectUnavailable)) throw err;
    return run(file, { onProgress, signal, contentType, wantDirect: false }); // storage refused direct uploads → use the API route
  }
}

async function run(file, { onProgress, signal, contentType, wantDirect }) {
  const { data: init } = await api.post("/upload/multipart/init", {
    filename: file.name,
    content_type: contentType,
    size: file.size,
    direct: wantDirect,
  });
  const { path, upload_id: uploadId } = init;
  const direct = !!init.direct;
  const PART_SIZE = direct ? DIRECT_PART_SIZE : WORKER_PART_SIZE;
  const CONCURRENCY = direct ? DIRECT_CONCURRENCY : WORKER_CONCURRENCY;

  const totalParts = Math.max(1, Math.ceil(file.size / PART_SIZE));
  const parts = new Array(totalParts);
  const loaded = new Array(totalParts).fill(0);
  let nextPart = 0;
  let failure = null;
  let finished = 0;
  const samples = [];

  const report = () => {
    const sum = loaded.reduce((a, b) => a + b, 0);
    const now = Date.now();
    samples.push([now, sum]);
    while (samples.length > 2 && now - samples[0][0] > 6000) samples.shift();
    const [t0, b0] = samples[0];
    const speed = now > t0 ? ((sum - b0) / (now - t0)) * 1000 : 0;
    onProgress?.(Math.min(1, sum / file.size), Math.max(0, speed));
  };

  const sendOnce = async (index, blob, ctl, poke) => {
    const onBytes = (n) => { loaded[index] = n; poke(); report(); };
    if (direct) {
      const { data } = await api.post("/upload/multipart/sign", { path, upload_id: uploadId, part: index + 1 }, { signal: ctl.signal });
      return putToR2(data.url, blob, index + 1, { signal: ctl.signal, onBytes });
    }
    const { data } = await api.put("/upload/multipart/part", blob, {
      params: { path, upload_id: uploadId, part: index + 1 },
      headers: { "Content-Type": "application/octet-stream" },
      timeout: 0,
      maxBodyLength: Infinity,
      signal: ctl.signal,
      onUploadProgress: (e) => onBytes(e.loaded),
    });
    return { part_number: data.part_number, etag: data.etag };
  };

  const sendPart = async (index) => {
    const blob = file.slice(index * PART_SIZE, Math.min(file.size, (index + 1) * PART_SIZE));
    let lastErr;
    for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
      if (signal?.aborted) throw abortError();
      loaded[index] = 0;
      const ctl = new AbortController();
      const onOuterAbort = () => ctl.abort();
      signal?.addEventListener("abort", onOuterAbort, { once: true });
      let timer;
      const poke = () => { clearTimeout(timer); timer = setTimeout(() => ctl.abort(), STALL_MS); };
      poke();
      try {
        const done = await sendOnce(index, blob, ctl, poke);
        loaded[index] = blob.size;
        report();
        return done;
      } catch (err) {
        if (signal?.aborted) throw abortError();
        lastErr = err; // a stall (our own abort) lands here too and is simply retried
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener("abort", onOuterAbort);
      }
      if (attempt < MAX_RETRIES) await new Promise((r) => setTimeout(r, attempt * 1500));
    }
    // nothing has ever got through to R2 from this browser → most likely a CORS / network block
    if (direct && finished === 0 && lastErr?.kind === "cors") throw new DirectUnavailable();
    throw lastErr;
  };

  const worker = async () => {
    while (!failure) {
      const index = nextPart++;
      if (index >= totalParts) return;
      try {
        parts[index] = await sendPart(index);
        finished++;
      } catch (err) {
        failure = failure || err;
        return;
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, totalParts) }, worker));

  if (failure) {
    api.post("/upload/multipart/abort", { path, upload_id: uploadId, direct }).catch(() => {});
    if (isAbort(failure)) throw abortError();
    if (failure instanceof DirectUnavailable) throw failure;
    throw new Error(failure.response?.data?.detail || "Upload failed — check your connection and try again.");
  }

  try {
    const { data } = await api.post("/upload/multipart/complete", { path, upload_id: uploadId, content_type: contentType, parts, direct, size: file.size });
    return { url: data.url, size: data.size };
  } catch (err) {
    throw new Error(err.response?.data?.detail || "Could not finish the upload.");
  }
}
